
import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // Hvis Supabase ikke er konfigurert, sett bare loading til false
    if (!isSupabaseConfigured) {
      if (mounted) {
        setUser(null);
        setLoading(false);
      }
      return;
    }

    // Hent gjeldende bruker med forbedret feilhåndtering
    const getUser = async () => {
      try {
        // Rens først eventuelle gamle tokens
        const storedSession = localStorage.getItem('sb-' + import.meta.env.VITE_PUBLIC_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
        if (storedSession) {
          try {
            const session = JSON.parse(storedSession);
            if (session?.refresh_token && session?.expires_at && new Date(session.expires_at * 1000) < new Date()) {
              // Token er utløpt, fjern det
              localStorage.removeItem('sb-' + import.meta.env.VITE_PUBLIC_SUPABASE_URL?.split('//')[1]?.split('.')[0] + '-auth-token');
            }
          } catch {
            // Ignorer parsing feil
          }
        }

        const { data: { user }, error } = await supabase.auth.getUser();
        
        if (!mounted) return;

        if (error) {
          if (error.message.includes('Invalid Refresh Token') || 
              error.message.includes('Refresh Token Not Found')) {
            // Rens session og sett bruker til null
            await supabase.auth.signOut();
            setUser(null);
          } else {
            console.warn('Auth error (non-critical):', error.message);
            setUser(null);
          }
        } else {
          setUser(user);
        }
      } catch (error: any) {
        if (!mounted) return;
        if (error?.message?.includes('Invalid Refresh Token') || 
            error?.message?.includes('Refresh Token Not Found')) {
          // Rens session og sett bruker til null
          try {
            await supabase.auth.signOut();
          } catch {
            // Ignorer feil ved utlogging
          }
          setUser(null);
        } else {
          console.warn('Failed to get user (non-critical):', error);
          setUser(null);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    getUser();

    // Lytt til auth endringer kun hvis Supabase er konfigurert
    let subscription: any = null;
    
    try {
      const { data } = supabase.auth.onAuthStateChange(
        (event, session) => {
          if (!mounted) return;
          
          try {
            if (event === 'TOKEN_REFRESHED' && !session) {
              // Token refresh feilet, logg ut bruker
              setUser(null);
              setLoading(false);
              return;
            }
            
            setUser(session?.user ?? null);
            setLoading(false);
          } catch (error) {
            console.warn('Auth state change error (non-critical):', error);
            setUser(null);
            setLoading(false);
          }
        }
      );
      subscription = data.subscription;
    } catch (error) {
      console.warn('Failed to setup auth listener (non-critical):', error);
      if (mounted) {
        setLoading(false);
      }
    }

    return () => {
      mounted = false;
      if (subscription && typeof subscription.unsubscribe === 'function') {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.warn('Failed to unsubscribe (non-critical):', error);
        }
      }
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Supabase ikke konfigurert' } };
    }

    try {
      // Rens først eventuelle gamle tokens
      await supabase.auth.signOut();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { data, error };
    } catch (error: any) {
      console.warn('Sign in error (non-critical):', error);
      return { data: null, error: { message: 'Innlogging feilet' } };
    }
  };

  const signUp = async (email: string, password: string) => {
    if (!isSupabaseConfigured) {
      return { data: null, error: { message: 'Supabase ikke konfigurert' } };
    }

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
      });
      return { data, error };
    } catch (error: any) {
      console.warn('Sign up error (non-critical):', error);
      return { data: null, error: { message: 'Registrering feilet' } };
    }
  };

  const signOut = async () => {
    if (!isSupabaseConfigured) {
      return { error: { message: 'Supabase ikke konfigurert' } };
    }

    try {
      const { error } = await supabase.auth.signOut();
      setUser(null);
      return { error };
    } catch (error: any) {
      console.warn('Sign out error (non-critical):', error);
      setUser(null); // Sett bruker til null uansett
      return { error: { message: 'Utlogging feilet' } };
    }
  };

  return {
    user,
    loading,
    signIn,
    signUp,
    signOut,
  };
};
